import React from 'react'
import { useTranslation } from 'react-i18next'
import { CMultiSelect } from '@coreui/react-pro'

export interface StrategyFilterOption {
  publicName: string
  publicStrategyId: string
}

interface StrategyFilterProps {
  /** The strategies the rows name, repeats and all. */
  strategies: StrategyFilterOption[]
  /** The column's value on each row must be the strategy's public strategy ID. */
  onChange: (filterFn: (item: string) => boolean) => void
  /** The public strategy IDs picked, for a page that picks them itself as well. */
  value?: string[]
  /** Told the public strategy IDs picked, each time they change. */
  onValueChange?: (publicStrategyIds: string[]) => void
}

/**
 * Column filter for a strategy. FilterMultiSelect offers the column's values as text, which for a
 * strategy is its name, and the sides of a strategy share a name. Here each option is one strategy,
 * drawn name over ID like the cells, and the search matches the label, so either part finds it.
 */
const StrategyFilter: React.FC<StrategyFilterProps> = ({ strategies, onChange, value, onValueChange }) => {
  const { t } = useTranslation('common')

  const unique = [
    ...new Map(
      strategies
        .filter((strategy) => strategy.publicStrategyId)
        .map((strategy) => [strategy.publicStrategyId, strategy]),
    ).values(),
  ].sort(
    (a, b) => a.publicName.localeCompare(b.publicName) || a.publicStrategyId.localeCompare(b.publicStrategyId),
  )

  return (
    <CMultiSelect
      size="sm"
      options={unique.map((strategy) => ({
        value: strategy.publicStrategyId,
        label: `${strategy.publicName} · ${strategy.publicStrategyId}`,
        publicName: strategy.publicName,
        publicStrategyId: strategy.publicStrategyId,
      }))}
      // A count, because a tag carries the whole label and a narrow column wraps it line by line.
      selectionType="counter"
      selectionTypeCounterText={t('filters.selected')}
      virtualScroller
      visibleItems={5}
      value={value}
      optionsTemplate={(option) => (
        <span className="d-flex flex-column">
          <span>{String(option.publicName)}</span>
          <span className="small text-body-secondary font-monospace text-nowrap">{String(option.publicStrategyId)}</span>
        </span>
      )}
      onChange={(selected) => {
        const chosen = selected.map((element) => String(element.value))
        onChange((item) => (chosen.length ? chosen.includes(item) : true))
        onValueChange?.(chosen)
      }}
    />
  )
}

export default StrategyFilter
